import axios from "axios";
import { API_BASE_URL } from "../Api/apiconnector";

/**
 * Fetch patient details by ID
 * @param {string} patientId - The patient ID
 * @param {string} doctorUsername - The doctor's username
 * @param {Function} setPatient - State setter for patient data
 */
export const fetchPatientDetails = async (patientId, doctorUsername, setPatient) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/patients_ecg/${patientId}/?username=${doctorUsername}`
    );
    setPatient(response.data);
  } catch (error) {
    console.error("Error fetching patient details:", error);
    alert("Failed to load patient details. Please try again.");
  }
};

/**
 * Preview the report before finalizing
 * @param {number} patientId - The patient ID
 * @param {string} doctorUsername - The doctor's username
 * @param {string} ecgFindings - ECG findings text
 * @param {string} additionalFindings - Additional findings text
 * @param {Object} editedPatientData - Edited patient information
 * @param {Function} setPreviewData - State setter for preview data
 */
export const previewReport = async (
  patientId,
  doctorUsername,
  ecgFindings,
  additionalFindings,
  editedPatientData,
  setPreviewData     // <-- CHANGED
) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/report/preview/`, {
      patient_id: patientId,
      doctor_username: doctorUsername,
      ecg_findings: ecgFindings,
      additional_findings: additionalFindings,
      edited_patient_data: editedPatientData
    });
    
    setPreviewData(response.data);   // <-- CHANGED
    
    // Scroll to preview section
    setTimeout(() => {
      const previewElement = document.querySelector('[aria-label="Close preview"]')?.closest('div');
      if (previewElement) {
        previewElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  } catch (error) {
    console.error("Error generating preview:", error);
    
    if (error.response?.data?.error) {
      alert(`Error: ${error.response.data.error}`);
    } else if (error.response?.data?.message) {
      alert(`Error: ${error.response.data.message}`);
    } else {
      alert("Failed to generate preview. Please check your inputs and try again.");
    }
  }
};

/**
 * Finalize and save the report
 * @param {number} patientId - The patient ID
 * @param {string} doctorUsername - The doctor's username
 * @param {string} ecgFindings - ECG findings text
 * @param {string} additionalFindings - Additional findings text
 * @param {Object} editedPatientData - Edited patient information
 * @returns {boolean} Success status
 */
// export const finalizeReport = async (
//   patientId,
//   doctorUsername,
//   ecgFindings,
//   additionalFindings,
//   editedPatientData
// ) => {
//   try {
//     const response = await axios.post(`${API_BASE_URL}/report/finalize/`, {
//       patient_id: patientId,
//       doctor_username: doctorUsername,
//       ecg_findings: ecgFindings,
//       additional_findings: additionalFindings,
//       edited_patient_data: editedPatientData
//     });
    
//     alert(response.data.message || "Report finalized successfully!");
//     return true;
//   } catch (error) {
//     console.error("Error finalizing report:", error);
    
//     if (error.response?.data?.error) {
//       alert(`Error: ${error.response.data.error}`);
//     } else if (error.response?.data?.message) {
//       alert(`Error: ${error.response.data.message}`);
//     } else {
//       alert("Failed to finalize report. Please try again.");
//     }
    
//     return false;
//   }
// };

export const finalizeReport = async (
  patientId,
  doctorUsername,
  ecgFindings,
  additionalFindings,
  editedPatientData
) => {
  try {
    await axios.post(`${API_BASE_URL}/report/finalize/`, {
      patient_id: patientId,
      doctor_username: doctorUsername,
      ecg_findings: ecgFindings,
      additional_findings: additionalFindings,
      edited_patient_data: editedPatientData
    });

    // ✅ No alert, just success
    return true;
  } catch (error) {
    console.error("Error finalizing report:", error);

    if (error.response?.data?.error) {
      alert(error.response.data.error);
    } else if (error.response?.data?.message) {
      alert(error.response.data.message);
    } else {
      alert("Failed to finalize report. Please try again.");
    }

    return false;
  }
};



export const getGreeting = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/greeting/`);
    return res.data.greeting;
  } catch (error) {
    console.error("Error fetching greeting:", error);
    return "";
  }
};

// Fetch locations
export const getLocations = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/get_locations/`);
    if (res.data.success) return res.data.locations;
    return [];
  } catch (error) {
    console.error("Error fetching locations:", error);
    return [];
  }
};

// Fetch patients
export const getPatients = async (doctorUsername, filters = {}) => {
  try {
    if (!doctorUsername) return [];

    let url = `${API_BASE_URL}/patients_ecg/?username=${doctorUsername}`;
    if (filters.location) url += `&location=${filters.location}`;
    if (filters.test_date) url += `&test_date=${filters.test_date}`;
    if (filters.search) url += `&search=${filters.search}`;

    const res = await axios.get(url);

    return res.data;
  } catch (error) {
    console.error("Error fetching patients:", error);
    return [];
  }
};

// Fetch doctor stats
export const getDoctorStats = async (doctorUsername) => {
  try {
    if (!doctorUsername) return {};
    const res = await axios.get(`${API_BASE_URL}/ecg_stat/?doctor_username=${doctorUsername}`);
    return res.data;
  } catch (error) {
    console.error("Error fetching stats:", error);
    return {};
  }
};


/**
 * Reject ECG report
 * @param {number} patientId
 * @param {string} doctorUsername
 * @param {string} reason
 * @returns {boolean}
 */
export const rejectReport = async (patientId, doctorUsername, reason) => {
  try {
    await axios.post(`${API_BASE_URL}/report_reject/`, {
      patient_id: patientId,
      doctor_username: doctorUsername,
      reason,
    });

    return true;
  } catch (error) {
    console.error("Error rejecting report:", error);
    return false;
  }
};